import { Timestamp } from "firebase/firestore";
import serializable, { propretyCustomSerializable } from "../common/decorators/serializable";
import { EntityFirebase } from "../common/entity.firebase";
import moment, { Moment } from "moment";

export type CashSessionStatus = "open" | "closed";

@serializable
export class CashSession extends EntityFirebase {
  @propretyCustomSerializable((value: Moment) => Timestamp.fromDate(value.toDate()))
  private openedAt: Moment;

  @propretyCustomSerializable((value: Moment | null) =>
    !value ? null : Timestamp.fromDate(value.toDate())
  )
  private closedAt: Moment | null;

  constructor(
    public readonly id: string,
    private status: CashSessionStatus,
    openedAt: Moment,
    closedAt: Moment | null,
    private openedByUserId: string,
    private closedByUserId: string | null,
    /** YYYY-MM-DD (dia de abertura) */
    private dayKey: string,
    private openingCash: number,
    private closingCash: number | null
  ) {
    super();
    this.openedAt = openedAt;
    this.closedAt = closedAt;
  }

  getStatus(): CashSessionStatus {
    return this.status;
  }

  getOpenedAt(): Moment {
    return this.openedAt;
  }

  getClosedAt(): Moment | null {
    return this.closedAt;
  }

  getOpenedByUserId(): string {
    return this.openedByUserId;
  }

  getClosedByUserId(): string | null {
    return this.closedByUserId;
  }

  getDayKey(): string {
    return this.dayKey;
  }

  getOpeningCash(): number {
    return this.openingCash;
  }

  getClosingCash(): number | null {
    return this.closingCash;
  }

  setClosed(closedAt: Moment, closedByUserId: string, closingCash: number) {
    this.status = "closed";
    this.closedAt = closedAt;
    this.closedByUserId = closedByUserId;
    this.closingCash = closingCash;
  }

  static async build(id: string, data: Record<string, unknown>) {
    const openedRaw = data.openedAt as { toDate?: () => Date } | undefined;
    const closedRaw = data.closedAt as { toDate?: () => Date } | null | undefined;
    /** `new this` preserva a classe gerada por `@serializable` (com `toObject`); `new CashSession` não. */
    return new this(
      id,
      (data.status as CashSessionStatus) || "open",
      openedRaw?.toDate ? moment(openedRaw.toDate()) : moment(),
      closedRaw?.toDate ? moment(closedRaw.toDate()) : null,
      typeof data.openedByUserId === "string" ? data.openedByUserId : "",
      typeof data.closedByUserId === "string" ? data.closedByUserId : null,
      typeof data.dayKey === "string" ? data.dayKey : moment().format("YYYY-MM-DD"),
      typeof data.openingCash === "number" ? data.openingCash : 0,
      typeof data.closingCash === "number" ? data.closingCash : null
    );
  }
}
